/**
 * MuteButton — Phase 7 Plan 07-06 Task 1 (UI-02 5th control).
 *
 * Persistent HUD button that toggles useNarrationStore.muted. Closes the UI-02
 * 5-control contract (era toggle + tour menu + sources trigger + return-to-
 * streetcar + mute = 5) so every action a user can take during the ride is
 * always one click away.
 *
 * Gating: only rendered when a tour is active (activeTourId !== null). No
 * active tour → no narration to mute → button hidden keeps the HUD uncluttered
 * during free-walk solitude.
 *
 * Chrome (AESTHETIC-BRIEF Rule 7, same tokens as every other HUD overlay):
 *   - Goudy Old Style / Cormorant Garamond / Georgia serif stack (typography.serif)
 *   - Cream #FDF6EE background (periodChrome.cream)
 *   - Espresso #3B2F2F text (periodChrome.espresso)
 *   - 1px warm-ochre #D4A87E border (periodChrome.ochre)
 *   - Subtle 2px shadow (no glassmorphism)
 *   - Border-radius 2px (not the modern 8-12px card radius)
 *
 * Position: fixed top:1rem, right:11rem — offset from ReturnToStreetcarButton
 * (top-right-most at top:1rem, right:1rem, width ~180px + 16px gap) so the
 * two top-right controls sit side-by-side without overlap.
 *
 * Interaction: click flips useNarrationStore.muted; NarrationPlayer's useEffect
 * reacts by pausing/resuming the <audio> element.
 */
import { useNarrationStore } from '../voiceover/useNarrationStore';
import { typography, periodChrome } from './PeriodChromeStyles';

export function MuteButton() {
  const activeTourId = useNarrationStore((s) => s.activeTourId);
  const muted = useNarrationStore((s) => s.muted);
  const setMuted = useNarrationStore((s) => s.setMuted);

  // Gate: hidden when no tour is active. Keeps the HUD to 4 controls during
  // free walk; expands to 5 the instant a tour starts.
  if (!activeTourId) return null;

  const label = muted ? 'Unmute narration' : 'Mute narration';

  return (
    <button
      type="button"
      onClick={() => setMuted(!muted)}
      aria-label={label}
      aria-pressed={muted}
      style={{
        position: 'fixed',
        top: '1rem',
        right: '11rem',
        zIndex: 40,
        padding: '10px 20px',
        background: periodChrome.cream,
        color: periodChrome.espresso,
        border: `1px solid ${periodChrome.ochre}`,
        fontFamily: typography.serif,
        fontSize: '0.9rem',
        letterSpacing: '0.05em',
        cursor: 'pointer',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(59,47,47,0.12)',
        minWidth: 180,
      }}
    >
      {label}
    </button>
  );
}
